/**
 * Prompts for the AI Marketing Assistant (Phase 2G).
 *
 * These are the prompts that shipped with the legacy
 * `ai-marketing-assistant` edge function, moved here unchanged in business
 * intent and wording. The ONLY deliberate transformation is that the three
 * structured operations no longer rely on Gemini tool-calling: they ask for an
 * explicit JSON object, which the OpenAI proxy serves through JSON mode.
 *
 * Prompt engineering is a product concern — no vendor names, no transport
 * details, no model ids in this file.
 */

import type { MarketingContentType, MarketingEventContext } from '../types';

export type { MarketingContentType, MarketingEventContext };

export const MARKETING_CONTENT_TYPES: readonly MarketingContentType[] = [
  'description',
  'social_caption',
  'email_draft',
  'hashtags',
];


export const MARKETING_LIMITS = {
  /** Matches the legacy edge function: description context was capped at 500. */
  maxDescriptionChars: 500,
  maxTitleChars: 200,
  /**
   * Output budget per operation.
   *
   * These must cover the WHOLE requested payload, not a rough guess: a budget
   * that runs out mid-generation returns a truncated (or, on reasoning models,
   * entirely empty) completion, which the platform correctly reports as a
   * failed generation.
   *   - description: 200-400 words of HTML  → ~1200
   *   - social_caption: 3 captions (≤100/200/300 chars) with emoji, in any of
   *     5 languages, JSON-escaped → 400 was not enough; ~900
   *   - email_draft: subject + preview + HTML body → 800 (unchanged)
   *   - hashtags: 10-15 short strings → 400 (default)
   */
  maxOutputTokens: {
    default: 400,
    description: 1200,
    social_caption: 900,
    email_draft: 800,
  } as Record<string, number>,
  /** Defensive output caps applied after parsing. */
  maxHtmlChars: 8000,
  maxCaptionChars: 600,
  maxSubjectChars: 200,
  maxPreviewChars: 300,
  maxHashtags: 15,
  maxHashtagChars: 60,
} as const;

export const marketingMaxOutputTokens = (type: MarketingContentType): number =>
  MARKETING_LIMITS.maxOutputTokens[type] ?? MARKETING_LIMITS.maxOutputTokens.default;

/** Structured operations need JSON mode; the description is free-form HTML. */
export const marketingWantsJson = (type: MarketingContentType): boolean =>
  type !== 'description';

/** The event context block, byte-for-byte compatible with the legacy prompt. */
export function marketingEventContext(event: MarketingEventContext): string {
  return `
Event: "${event.title}"
Category: ${event.category || 'General'}
Location: ${event.location || 'TBD'}
Venue: ${event.venue || ''}
Date: ${event.startDate ?? ''}${event.endDate ? ` to ${event.endDate}` : ''}
Price: ${event.price || 'TBD'}
Tickets: ${event.totalTickets ?? 0} total, ${event.availableTickets ?? 0} remaining
Description: ${(event.description || '').slice(0, MARKETING_LIMITS.maxDescriptionChars)}
`.trim();
}

export interface MarketingPromptInput {
  contentType: MarketingContentType;
  event: MarketingEventContext;
  language: string;
  tone: string;
}

export function marketingSystemPrompt({
  contentType,
  language,
  tone,
}: MarketingPromptInput): string {
  switch (contentType) {
    case 'description':
      return `You are an expert event copywriter. Write a compelling, engaging event description that drives ticket sales. Tone: ${tone}. Language: ${language}. Include a hook, key details, and a call-to-action. Use HTML formatting (<p>, <strong>, <ul>) for rich text. 200-400 words. Output only the HTML, with no markdown fence and no commentary.`;
    case 'social_caption':
      return `You are a social media expert for events. Create 3 different social media captions (short, medium, long) for promoting this event. Tone: ${tone}. Language: ${language}. Include emojis and be engaging. Respond with JSON only: {"short": "...", "medium": "...", "long": "..."} where short is under 100 characters, medium is 100-200 characters and long is 200-300 characters. No prose, no markdown.`;
    case 'email_draft':
      return `You are an email marketing expert. Write a promotional email for this event. Tone: ${tone}. Language: ${language}. Include subject line, preview text, and email body in HTML. Respond with JSON only: {"subject": "...", "preview_text": "...", "body_html": "..."}. No prose, no markdown fence.`;
    case 'hashtags':
      return `You are a social media strategist. Generate 10-15 relevant hashtags for promoting this event. Mix popular broad hashtags with specific niche ones. Language: ${language}. Respond with JSON only: {"hashtags": ["...", "..."]} where each entry is a hashtag without the leading #. No prose, no markdown.`;
    default:
      return '';
  }
}

export function marketingUserPrompt({ contentType, event }: MarketingPromptInput): string {
  const context = marketingEventContext(event);
  switch (contentType) {
    case 'description':
      return `Write an event description for:\n${context}`;
    case 'social_caption':
      return `Create social media captions for:\n${context}`;
    case 'email_draft':
      return `Write a promotional email for:\n${context}`;
    case 'hashtags':
      return `Generate hashtags for:\n${context}`;
    default:
      return context;
  }
}
